import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Upload,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X
} from 'lucide-react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../features/auth/authThunks';
import { selectDisplayName } from '../../features/auth/authSelectors';

const hasChildren = (item) => Array.isArray(item.children) && item.children.length > 0;

/** The sections holding the page at `pathname`, as `{ [key]: true }`. */
const sectionsContaining = (navigation, pathname) =>
  Object.fromEntries(
    navigation
      .filter((item) => hasChildren(item) && item.children.some((child) => pathname.startsWith(child.to)))
      .map((item) => [item.key, true])
  );

const NavItem = ({ to, icon: Icon, label, collapsed }) => {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => clsx(
        "flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 text-sm font-medium",
        isActive
          ? "bg-ninja-greenLight text-ninja-green font-semibold"
          : "text-text-secondary hover:bg-ninja-gray hover:text-ninja-dark dark:hover:bg-[#2d2d2d] dark:hover:text-white",
        collapsed && "justify-center px-3"
      )}
      title={collapsed ? label : undefined}
    >
      <div className="flex items-center justify-center w-5 h-5 text-[18px]">
        {Icon && <Icon size={20} />}
      </div>
      {!collapsed && <span className="whitespace-nowrap transition-opacity duration-200">{label}</span>}
    </NavLink>
  );
};

const SubNavLink = ({ to, children, end = false }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => clsx(
      "py-2 px-4 text-sm font-medium rounded-lg transition-colors",
      isActive
        ? "text-ninja-green font-semibold"
        : "text-text-secondary hover:text-ninja-dark dark:hover:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]"
    )}
  >
    {children}
  </NavLink>
);

/**
 * Menu entry that both navigates and expands.
 *
 * The label opens the section's own page; the chevron opens its children. They
 * are two separate controls because nesting a button inside a link is invalid
 * markup, and because opening the page should not have closing the submenu as a
 * side effect.
 *
 * With the sidebar collapsed there is no room for the chevron, so it degrades to
 * a plain `NavItem` — the same thing a group does with its submenu.
 *
 * @param {object} props
 * @param {string} props.to - The section's own page.
 * @param {string[]} [props.activeRoutes] - Routes that keep the section highlighted.
 *   Defaults to `to` alone; a section whose children live elsewhere must list them.
 */
const CollapsibleNavItem = ({
  to,
  icon: Icon,
  label,
  collapsed,
  isOpen,
  onToggle,
  activeRoutes,
  children,
}) => {
  const location = useLocation();

  if (collapsed) {
    return <NavItem to={to} icon={Icon} label={label} collapsed />;
  }

  const isActive = (activeRoutes || [to]).some((route) => location.pathname.startsWith(route));

  return (
    <div className="flex flex-col">
      <div className={clsx(
        "flex items-center rounded-xl transition-all duration-200",
        isActive
          ? "bg-ninja-greenLight text-ninja-green font-semibold"
          : "text-text-secondary hover:bg-ninja-gray hover:text-ninja-dark dark:hover:bg-[#2d2d2d] dark:hover:text-white"
      )}>
        <NavLink to={to} className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-sm font-medium">
          <div className="flex items-center justify-center w-5 h-5 text-[18px]">
            {Icon && <Icon size={20} />}
          </div>
          <span className="whitespace-nowrap">{label}</span>
        </NavLink>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={`${isOpen ? 'Contraer' : 'Expandir'} ${label}`}
          className="shrink-0 px-3 py-3 rounded-r-xl"
        >
          <ChevronDown size={16} className={clsx("transition-transform duration-200", isOpen && "rotate-180")} />
        </button>
      </div>

      {isOpen && (
        <div className="flex flex-col space-y-1 mt-1 px-4 border-l-2 border-border ml-6">
          {children}
        </div>
      )}
    </div>
  );
};

/** Menu entry with no page of its own: clicking it only opens its children. */
const NavGroup = ({ icon: Icon, label, collapsed, isOpen, onToggle, children }) => (
  <div className="flex flex-col">
    <button
      onClick={onToggle}
      className={clsx(
        "flex items-center py-3 rounded-xl cursor-pointer transition-all duration-200 text-sm font-medium w-full",
        // One or the other, never both: two `justify-*` utilities in the same
        // class list are decided by the order inside the stylesheet, not by the
        // order written here, and `justify-between` was winning — with the
        // chevron hidden, its only child ended up against the left edge while
        // every other icon sat centred.
        collapsed ? "justify-center px-3" : "justify-between px-4",
        isOpen && !collapsed
          ? "bg-ninja-greenLight text-ninja-green"
          : "text-text-secondary hover:bg-ninja-gray hover:text-ninja-dark dark:hover:bg-[#2d2d2d] dark:hover:text-white"
      )}
      title={collapsed ? label : undefined}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-5 h-5 text-[18px]">
          {Icon && <Icon size={20} />}
        </div>
        {!collapsed && <span className="whitespace-nowrap">{label}</span>}
      </div>
      {!collapsed && (
        <ChevronDown size={16} className={clsx("transition-transform duration-200", isOpen ? "rotate-180" : "")} />
      )}
    </button>

    {!collapsed && isOpen && (
      <div className="flex flex-col space-y-1 mt-1 px-4 border-l-2 border-border ml-6">
        {children}
      </div>
    )}
  </div>
);

/**
 * The sidebar of every signed-in profile. The menu is data handed in by the
 * workspace, so a profile's entries are never a branch inside this component.
 *
 * Each entry is one of:
 * - `{ type: 'link', to, icon, label }` — a page.
 * - `{ type: 'section', key, to, icon, label, children }` — a page whose
 *   children open under it.
 * - `{ type: 'group', key, icon, label, children }` — only a container.
 *
 * Children are `{ to, label, end? }`.
 *
 * @param {object} props
 * @param {Array<object>} props.navigation - Must be a module-level constant: the
 *   effect that opens each submenu depends on it.
 * @param {string} [props.areaLabel] - Names the area this menu belongs to
 *   ("OMS", "WMS"), under the logo.
 * @param {{to: string, label: string, icon: React.ComponentType}|null} [props.areaSwitch] -
 *   The door to the other area, right under the user. `null` when the account
 *   has only one side.
 * @param {boolean} [props.showUpload=true] - Whether the "Carga tus órdenes"
 *   shortcut is offered. It is, on both sides: an order is loaded the same way.
 * @param {boolean} [props.mobileOpen] - Below `md` the sidebar is a drawer over
 *   the page, and this says whether it is showing.
 * @param {() => void} [props.onCloseMobile]
 */
export const Sidebar = ({
  collapsed,
  setCollapsed,
  navigation,
  areaLabel,
  areaSwitch = null,
  showUpload = true,
  mobileOpen = false,
  onCloseMobile,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const displayName = useSelector(selectDisplayName);

  const [openSections, setOpenSections] = useState(() =>
    sectionsContaining(navigation, location.pathname)
  );

  // A submenu opens on its own when one of its pages is reached, but it is never
  // closed on leaving: closing it is the user's call.
  useEffect(() => {
    const reached = sectionsContaining(navigation, location.pathname);
    setOpenSections((prev) =>
      Object.keys(reached).every((key) => prev[key]) ? prev : { ...prev, ...reached }
    );
  }, [navigation, location.pathname]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = () => {
    dispatch(logoutUser(navigate));
  };

  const renderChildren = (children) =>
    children.map((child) => (
      <SubNavLink key={child.to} to={child.to} end={child.end}>{child.label}</SubNavLink>
    ));

  return (
    <aside className={clsx(
      "fixed inset-y-0 left-0 h-screen z-50 bg-white dark:bg-[#1a1a1a] border-r border-border flex flex-col transition-all duration-300",
      // Collapsing is a desktop choice; the drawer always opens with its labels.
      collapsed ? "w-[80px]" : "w-[240px]",
      mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
    )}>
      {/* Hidden on a phone: there the sidebar is a drawer, and the handle would
          float over the page it covers. */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? 'Expandir el menú' : 'Contraer el menú'}
        className="absolute -right-3 top-7 w-6 h-6 rounded-full bg-white dark:bg-[#2d2d2d] border border-border cursor-pointer hidden md:flex items-center justify-center text-text-muted shadow-sm hover:bg-ninja-green hover:text-white hover:border-ninja-green transition-all z-10"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Header & User Profile */}
      <div className="px-4 py-6 border-b border-border flex flex-col gap-4">
        <div className="w-full flex items-center justify-between gap-2 text-xl font-extrabold text-ninja-green cursor-pointer">
          {collapsed ? 'NH' : <span className="text-2xl tracking-tight">Ninja{' '}<span className="text-ninja-dark dark:text-white">Hubs</span></span>}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Cerrar el menú"
            data-testid="sidebar-close"
            className="md:hidden text-text-muted hover:text-ninja-dark dark:hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {!collapsed && areaLabel && (
          <span
            data-testid="sidebar-area"
            className="self-start px-2.5 py-1 rounded-lg bg-ninja-greenLight dark:bg-ninja-green/20 text-ninja-green text-xs font-bold uppercase tracking-wider"
          >
            {areaLabel}
          </span>
        )}

        {!collapsed && user && (
          <div className="flex flex-col gap-1 mt-2">
            <span className="text-sm font-medium text-ninja-dark dark:text-white">
              Hola, {displayName}
            </span>
            <span className="text-xs text-text-muted">
              {user.user_type?.[0] || 'admin'}
            </span>
          </div>
        )}

        {/* The way across to the other area. Not a menu entry: the menu lists
            the places of this side, and the other side is not one of them. */}
        {areaSwitch && (
          <NavLink
            to={areaSwitch.to}
            data-testid="sidebar-area-switch"
            title={collapsed ? areaSwitch.label : undefined}
            className={clsx(
              "flex items-center gap-2 rounded-xl border border-border text-sm font-semibold text-ninja-dark dark:text-white hover:border-ninja-green hover:text-ninja-green transition-colors",
              collapsed ? "justify-center px-2 py-2" : "px-3 py-2"
            )}
          >
            {areaSwitch.icon && <areaSwitch.icon size={16} />}
            {!collapsed && <span className="whitespace-nowrap">{areaSwitch.label}</span>}
          </NavLink>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden pb-4">
        {/* Mi Cuenta & Upload Button */}
        <div className="px-4 py-4 flex flex-col gap-3">
          {!collapsed && (
            <NavLink to="/account" className="text-sm font-medium text-text-secondary hover:text-ninja-dark dark:hover:text-white transition-colors px-2">
              Mi Cuenta
            </NavLink>
          )}
          {showUpload && (
            <button
              onClick={() => navigate('/upload')}
              className={clsx(
                "w-full bg-ninja-dark dark:bg-white text-white dark:text-ninja-gray2 rounded-xl py-3 flex items-center gap-2 hover:opacity-90 transition-opacity font-semibold text-sm",
                collapsed ? "justify-center px-3" : "px-4"
              )}
              title={collapsed ? "Carga tus órdenes" : undefined}
            >
              <Upload size={18} />
              {!collapsed && "Carga tus órdenes"}
            </button>
          )}
        </div>

        <div className="h-px bg-border mx-4 mb-2" />

        {/* Navigation Items. Same side padding as the upload button above and
            the logout below, so every icon shares one vertical line and the
            rows end up exactly as wide as the button. */}
        <div className="px-4 flex flex-col space-y-1">
          {navigation.map((item) => {
            if (item.type === 'section') {
              return (
                <CollapsibleNavItem
                  key={item.key}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  collapsed={collapsed}
                  isOpen={Boolean(openSections[item.key])}
                  onToggle={() => toggleSection(item.key)}
                  activeRoutes={item.children.map((child) => child.to)}
                >
                  {renderChildren(item.children)}
                </CollapsibleNavItem>
              );
            }

            if (item.type === 'group') {
              return (
                <NavGroup
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  collapsed={collapsed}
                  isOpen={Boolean(openSections[item.key])}
                  onToggle={() => toggleSection(item.key)}
                >
                  {renderChildren(item.children)}
                </NavGroup>
              );
            }

            return (
              <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label} collapsed={collapsed} />
            );
          })}
        </div>
      </div>

      {/* Footer Logout */}
      <div className="mt-auto p-4 border-t border-border">
        <button onClick={handleLogout} className={clsx(
          "w-full flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all text-danger text-sm font-medium border-none bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20",
          collapsed && "justify-center px-3"
        )}>
          <div className="flex items-center justify-center w-5 h-5 text-[18px]">
            <LogOut size={20} />
          </div>
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>
    </aside>
  );
};
